import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import WaveSurfer from "wavesurfer.js";
import ZoomPlugin from "wavesurfer.js/dist/plugins/zoom";
import Timeline from "wavesurfer.js/dist/plugins/timeline";
import {
  setIsPlaying,
  setWaveformLoading,
  setZoom,
  setMinZoom,
  setMaxZoom,
} from "../../../state/radioReducer";

export default function RadioWaveform() {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const zoomPluginRef = useRef<ReturnType<typeof ZoomPlugin.create> | null>(
    null,
  );
  const [hasError, setHasError] = useState(false);
  const dispatch = useDispatch();

  const isWaveformLoading = useSelector(
    (state: RootState) => state.radio?.isWaveformLoading,
    isEqual,
  );

  const activeURL = useSelector(
    (state: RootState) => state.radio?.activeURL,
    isEqual,
  );
  const isPlaying = useSelector(
    (state: RootState) => state.radio?.isPlaying,
    isEqual,
  );
  const skipForwardCount = useSelector(
    (state: RootState) => state.radio?.skipForwardCount,
    isEqual,
  );
  const skipBackwardCount = useSelector(
    (state: RootState) => state.radio?.skipBackwardCount,
    isEqual,
  );
  const volume = useSelector(
    (state: RootState) => state.radio?.volume ?? 1,
    isEqual,
  );
  const playbackSpeed = useSelector(
    (state: RootState) => state.radio?.playbackSpeed ?? 1,
    isEqual,
  );
  const zoom = useSelector(
    (state: RootState) => state.radio?.zoom ?? 100,
    isEqual,
  );

  useEffect(() => {
    console.log("Zoom level changed:", zoom);
  });

  useEffect(() => {
    if (!waveformRef.current || !activeURL) {
      return;
    }

    dispatch(setWaveformLoading(true));
    setHasError(false);

    wavesurferRef.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#525252",
      progressColor: "#a18b6b",
      cursorColor: "#ffffff",
      height: "auto",
      dragToSeek: true,
      minPxPerSec: 10,
      fillParent: false,
      cursorWidth: 2,
      barWidth: 5,
      barGap: 2,
      barRadius: 5,
    });

    dispatch(setIsPlaying(false));
    wavesurferRef.current.load(activeURL).catch((error) => {
      // Ignore abort errors when switching tracks
      if (error?.name === "AbortError") return;
      try {
        wavesurferRef.current?.stop();
        wavesurferRef.current?.destroy();
        wavesurferRef.current = null;
      } catch {
        // Ignore errors during cleanup
      }
      setHasError(true);
      dispatch(setWaveformLoading(false));
      dispatch(setIsPlaying(false));
    });

    wavesurferRef.current.on("ready", () => {
      dispatch(setWaveformLoading(false));
      // Register plugins only after audio is loaded
      if (wavesurferRef.current && waveformRef.current) {
        // Calculate min zoom based on container and duration
        const duration = wavesurferRef.current.getDuration();
        const containerWidth = waveformRef.current.clientWidth;
        const calculatedMinZoom = containerWidth / duration;
        const maxZoom = 200;

        dispatch(setMinZoom(calculatedMinZoom));
        dispatch(setMaxZoom(maxZoom));

        // Reset zoom to default for new waveform
        const defaultZoom = 100;
        dispatch(setZoom(defaultZoom));
        wavesurferRef.current.zoom(defaultZoom);

        zoomPluginRef.current = wavesurferRef.current.registerPlugin(
          ZoomPlugin.create({
            scale: 0.025,
            maxZoom: maxZoom,
            deltaThreshold: 5,
          }),
        );
        wavesurferRef.current.registerPlugin(
          Timeline.create({
            height: 20,
            style: {
              color: "#ffffff",
              fontFamily: '"JaguarExuberant", "Helvetica", "Arial", sans-serif',
            },
          }),
        );
        // Sync slider with wheel zoom
        wavesurferRef.current.on("zoom", (minPxPerSec: number) => {
          dispatch(setZoom(minPxPerSec));
        });
      }
    });

    wavesurferRef.current.on("pause", () => {
      dispatch(setIsPlaying(false));
    });
    wavesurferRef.current.on("play", () => {
      dispatch(setIsPlaying(true));
    });
    wavesurferRef.current.on("error", (error) => {
      // Ignore abort errors when switching tracks
      if (error?.message?.includes("aborted")) return;
      try {
        wavesurferRef.current?.stop();
        wavesurferRef.current?.destroy();
        wavesurferRef.current = null;
      } catch {
        // Ignore errors during cleanup
      }
      setHasError(true);
      dispatch(setWaveformLoading(false));
      dispatch(setIsPlaying(false));
    });

    return () => {
      try {
        wavesurferRef.current?.stop();
        wavesurferRef.current?.destroy();
      } catch {
        // Ignore errors during cleanup
      }
      zoomPluginRef.current = null;
      dispatch(setIsPlaying(false));
    };
  }, [activeURL, dispatch]);

  useEffect(() => {
    if (!wavesurferRef.current) return;
    if (isPlaying) {
      wavesurferRef.current.play();
    } else {
      wavesurferRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!wavesurferRef.current || skipForwardCount === 0) return;
    wavesurferRef.current.skip(10);
  }, [skipForwardCount]);

  useEffect(() => {
    if (!wavesurferRef.current || skipBackwardCount === 0) return;
    wavesurferRef.current.skip(-10);
  }, [skipBackwardCount]);

  useEffect(() => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.setVolume(volume);
  }, [volume]);

  useEffect(() => {
    if (!wavesurferRef.current) return;
    wavesurferRef.current.setPlaybackRate(playbackSpeed);
  }, [playbackSpeed]);

  useEffect(() => {
    if (!wavesurferRef.current) return;
    // Only zoom if audio is loaded (duration > 0)
    if (wavesurferRef.current.getDuration() > 0) {
      wavesurferRef.current.zoom(zoom);
    }
  }, [zoom]);

  return (
    <div className={styles.container}>
      {isWaveformLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}
      {hasError && (
        <div className={styles.errorOverlay}>
          <span>Radio unavailable</span>
        </div>
      )}
      <div
        ref={waveformRef}
        className={styles.waveform}
        style={{ display: hasError ? "none" : undefined }}
      ></div>
    </div>
  );
}
